<template>
    <div>
        <PageHeader>
            {{ $t('catalog') }} - {{ $t('product') }}: {{ '#' + productId }}

            <template #side>
                <ButtonIcon icon="arrow-left" variant="light" @click="$router.go(-1)">
                    {{ $t('goBack') }}
                </ButtonIcon>
            </template>
        </PageHeader>

        <ErrorMessage :value="error" />
        <Card :header="$t('product')" class="mb-4">
            <b-overlay :show="loadingProduct || submitting" opacity="0">
                <b-form @submit.prevent>
                    <FormValidator :validator="$v.form.name" form-group v-slot="s" :label="$t('name')">
                        <b-form-input v-model="form.name" :state="s.state" />
                    </FormValidator>

                    <FormValidator :validator="$v.form.author" form-group v-slot="s" :label="$t('author')">
                        <b-form-input v-model="form.author" :state="s.state" />
                    </FormValidator>

                    <FormValidator :validator="$v.form.description" form-group v-slot="s" :label="$t('description')">
                        <b-form-textarea v-model="form.description" :state="s.state" />
                    </FormValidator>

                    <b-form-group :label="$t('source')">
                        <b-form-input v-model="form.source" />
                    </b-form-group>

                    <b-form-group :label="$t('brand')">
                        <Multiselect
                            v-model="form.brand"
                            :fetch-options-function="fetchBrands"
                            :parse-options-function="parseBrands"
                            :placeholder="$t('selectField.placeholder')"
                            asynchronous
                            searchable
                            track-by="id"
                            label="name"
                            emit-initial-value
                            @loading="v => (isBrandsLoading = v)"
                            @search-change="v => (brandsSearchText = v)"
                        />
                    </b-form-group>

                    <b-form-group :label="$t('categories')">
                        <Multiselect
                            v-model="form.categories"
                            :fetch-options-function="fetchCategories"
                            :parse-options-function="parseCategories"
                            :placeholder="$t('selectField.placeholder')"
                            asynchronous
                            searchable
                            multiple
                            show-checkboxes
                            track-by="id"
                            label="name"
                            emit-initial-value
                            :clear-on-select="false"
                            :close-on-select="false"
                            :show-selected="true"
                            @loading="v => (isCategoriesLoading = v)"
                            @search-change="v => (categoriesSearchText = v)"
                        />
                    </b-form-group>
                </b-form>

                <ButtonIcon class="ml-2" icon="save" variant="primary" :loading="submitting" @click="handleSubmit()">
                    {{ $t('save') }}
                </ButtonIcon>
            </b-overlay>
        </Card>

        <Card :header="$t('variants')">
            <DataTable
                :fields="variantFields"
                :items="variants.items"
                :loading="loadingVariants"
                :total-rows="variants.max"
                :current-page.sync="variantsPage"
                :per-page.sync="variantsPerPage"
                no-sort-reset
                no-local-sorting
            >
                <template #cell(product)="{ item }">
                    {{ item.product.name }}
                </template>

                <template #cell(description)="{ item }">
                    <div v-if="item.description" class="d-flex gap-2">
                        <TruncatedTextModal :text="item.description" :button-title="$t('showWholeDescription')" :modal-title="$t('description')" />
                    </div>
                </template>

                <template #cell(images)="{ item }">
                    <div class="d-flex gap-2 flex-wrap">
                        <ImageLoader
                            v-for="image in item.images"
                            :key="image.id"
                            :src="image.url"
                            width="50"
                            height="50"
                            preview
                        />
                    </div>
                </template>

                <template #cell(action)="data">
                    <div class="d-flex gap-1 justify-content-end">
                        <ButtonIcon size="sm" icon="eye" variant="info" @click="$router.push({ name: 'CatalogProductVariantsDetails', params: { id: data.item.gtin } })" />
                    </div>
                </template>
            </DataTable>
        </Card>
    </div>
</template>

<script>
    import { required } from 'vuelidate/lib/validators';
    import DataTable from '@/components/common/DataTable.vue';
    import FormValidator from '@/components/common/FormValidator.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import ImageLoader from '@/components/common/ImageLoader.vue';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import TruncatedTextModal from '@/components/common/TruncatedTextModal.vue';

    export default {
        name: 'CatalogProductEdit',
        components: {
            DataTable,
            FormValidator,
            Multiselect,
            ImageLoader,
            ErrorMessage,
            TruncatedTextModal,
        },
        data() {
            return {
                productId: this.$route.params.id,
                submitting: false,
                error: null,
                loadingProduct: false,

                form: {
                    name: '',
                    author: '',
                    description: '',
                    source: '',
                    brand: null,
                    categories: [],
                },

                brandsSearchText: '',
                isBrandsLoading: false,
                categoriesSearchText: '',
                isCategoriesLoading: false,

                variants: { items: [], max: 0 },
                loadingVariants: false,
                variantsPage: 1,
                variantsPerPage: 10,
            };
        },
        validations() {
            return {
                form: {
                    name: { required },
                    author: {},
                    description: { required },
                },
            };
        },
        computed: {
            variantFields() {
                return [
                    { key: 'gtin', label: this.$t('gtin') },
                    { key: 'code', label: this.$t('code') },
                    { key: 'product', label: this.$t('product') },
                    { key: 'manufacturer.name', label: this.$t('manufacturer') },
                    { key: 'url', label: this.$t('url') },
                    { key: 'description', label: this.$t('description') },
                    { key: 'images', label: this.$t('images') },
                    {
                        key: 'action',
                        label: '',
                        tdClass: 'text-right',
                        thClass: 'text-right',
                    },
                ];
            },
            variantsParams() {
                return {
                    product: this.productId,
                    page: this.variantsPage,
                    perPage: this.variantsPerPage,
                };
            },
        },
        watch: {
            variantsParams: { handler: 'fetchVariants', deep: true },
            productId: {
                handler() {
                    this.variantsPage = 1;
                },
            },
        },
        methods: {
            async fetchProduct() {
                this.error = null;
                this.loadingProduct = true;
                try {
                    const p = await this.$req('catalog/Products').get({ urlParams: { id: this.productId } });

                    this.form = {
                        ...p,
                        brand: p.brand || null,
                        categories: p.categories || [],
                    };
                } catch (e) {
                    this.error = e;
                } finally {
                    this.loadingProduct = false;
                }
            },
            async fetchVariants() {
                this.error = null;
                this.loadingVariants = true;
                try {
                    this.variants = await this.$req('catalog/Variants').get({ requestParams: this.variantsParams });
                } catch (e) {
                    this.error = e;
                } finally {
                    this.loadingVariants = false;
                }
            },

            async handleSubmit() {
                this.$v.$touch();
                if (this.$v.$invalid) return;

                this.error = null;
                this.submitting = true;
                try {
                    const payload = {
                        author: this.form.author,
                        description: this.form.description,
                        name: this.form.name,
                        source: this.form.source,

                        brand: this.form.brand?.id,
                        categories: this.form.categories?.map(c => c.id),
                    };

                    await this.$req('catalog/Products').patch({ urlParams: { id: this.productId }, requestParams: payload });

                    this.$toastr.success(this.$t('updateSuccess'));
                } catch (e) {
                    this.error = e;
                } finally {
                    this.submitting = false;
                }
            },

            fetchBrands() {
                return this.$req('catalog/Brands').get({ requestParams: { name: this.brandsSearchText } });
            },
            parseBrands(d) {
                return d?.items || [];
            },

            fetchCategories() {
                return this.$req('catalog/Categories').get({ requestParams: { name: this.categoriesSearchText } });
            },
            parseCategories(d) {
                return d?.items || [];
            },
        },
        created() {
            this.fetchProduct();
            this.fetchVariants();
        },
    };
</script>
